import React, { useState, useMemo, useCallback } from 'react';
import { Layout, Menu, Button, Drawer, Dropdown, Avatar, Space } from 'antd';
import type { MenuProps } from 'antd';
import { ROLE_ROUTE_MAP } from '../config/role-routes';
import { type EmployeeRole, EMPLOYEE_ROLES } from '../types/employee.types';

/** Set of valid EmployeeRole values for O(1) runtime validation */
const VALID_ROLES = new Set<string>(EMPLOYEE_ROLES);
import {
  InboxOutlined,
  UserOutlined,
  ShoppingOutlined,
  ShopOutlined,
  TeamOutlined,
  ApartmentOutlined,
  BarsOutlined,
  DownloadOutlined,
  UploadOutlined,
  SearchOutlined,
  AlertOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  TruckOutlined,
  FileTextOutlined,
  PrinterOutlined,
  DollarCircleOutlined,
  RollbackOutlined,
  ContainerOutlined,
  FileDoneOutlined,
  SwapOutlined,
  ToolOutlined,
  PartitionOutlined,
  WalletOutlined,
  CreditCardOutlined,
  BankOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  FundOutlined,
  DatabaseOutlined,
  AppstoreOutlined,
  AccountBookOutlined,
  TagsOutlined,
  ColumnHeightOutlined,
  LogoutOutlined,
  LockOutlined,
  AuditOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/useBreakpoint';
import { NavSearch } from './NavSearch';
import { LocaleSwitcher } from '../i18n/LocaleSwitcher';
import { useAuthStore } from '../../features/auth/stores/authStore';
import { authService } from '../../features/auth/services/auth.service';
import { ChangePasswordModal } from '../../features/auth/components/ChangePasswordModal';

const { Header, Sider, Content } = Layout;

const ALL_GROUP_KEYS = [
  'master-data-group',
  'inventory-group',
  'sales-group',
  'procurement-group',
  'production-group',
  'finance-group',
  'reports-group',
  'documents-group',
];

/**
 * Get menu items with translations
 * Uses SubMenu (no type:'group') for collapsible behavior
 */
const getMenuItems = (t: (key: string, options?: Record<string, unknown>) => string): MenuProps['items'] => [
  {
    key: 'master-data-group',
    label: t('nav:masterData.title'),
    icon: <DatabaseOutlined />,
    children: [
      { key: '/materials', icon: <InboxOutlined />, label: t('nav:masterData.materials') },
      { key: '/customers', icon: <UserOutlined />, label: t('nav:masterData.customers') },
      { key: '/suppliers', icon: <ShoppingOutlined />, label: t('nav:masterData.suppliers') },
      { key: '/warehouses', icon: <ShopOutlined />, label: t('nav:masterData.warehouses') },
      { key: '/departments', icon: <ApartmentOutlined />, label: t('nav:masterData.departments') },
      { key: '/employees', icon: <TeamOutlined />, label: t('nav:masterData.employees') },
      { type: 'divider' as const },
      { key: '/categories', icon: <TagsOutlined />, label: t('nav:masterData.categories') },
      { key: '/units-of-measure', icon: <ColumnHeightOutlined />, label: t('nav:masterData.unitsOfMeasure') },
    ],
  },
  {
    key: 'inventory-group',
    label: t('nav:inventory.title'),
    icon: <AppstoreOutlined />,
    children: [
      { key: '/inbound-documents', icon: <DownloadOutlined />, label: t('nav:inventory.inbound') },
      { key: '/outbound-documents', icon: <UploadOutlined />, label: t('nav:inventory.outbound') },
      { key: '/counting-documents', icon: <SearchOutlined />, label: t('nav:inventory.stockCounting') },
      { key: '/inventory-alerts', icon: <AlertOutlined />, label: t('nav:inventory.alerts') },
      { key: '/inventory-balances', icon: <DollarOutlined />, label: t('nav:inventory.balance') },
    ],
  },
  {
    key: 'sales-group',
    label: t('nav:sales.title'),
    icon: <ShoppingCartOutlined />,
    children: [
      { key: '/sales-orders', icon: <ShoppingCartOutlined />, label: t('nav:sales.orders') },
      { key: '/shipments', icon: <TruckOutlined />, label: t('nav:sales.shipments') },
      { key: '/quotations', icon: <FileTextOutlined />, label: t('nav:sales.quotations') },
      { key: '/price-lists', icon: <DollarCircleOutlined />, label: t('nav:sales.priceLists') },
      { key: '/special-price-requests', icon: <AuditOutlined />, label: t('nav:sales.specialPriceRequests') },
      { key: '/returns', icon: <RollbackOutlined />, label: t('nav:sales.returns') },
      { key: '/statements', icon: <FileTextOutlined />, label: t('nav:sales.statements') },
    ],
  },
  {
    key: 'procurement-group',
    label: t('nav:procurement.title'),
    icon: <ContainerOutlined />,
    children: [
      { key: '/purchase-orders', icon: <ContainerOutlined />, label: t('nav:procurement.orders') },
      { key: '/purchase-receipts', icon: <FileDoneOutlined />, label: t('nav:procurement.receipts') },
      { key: '/purchase-returns', icon: <SwapOutlined />, label: t('nav:procurement.returns') },
    ],
  },
  {
    key: 'production-group',
    label: t('nav:production.title'),
    icon: <ToolOutlined />,
    children: [
      { key: '/production/boms', icon: <PartitionOutlined />, label: t('nav:production.boms') },
      { key: '/production', icon: <ToolOutlined />, label: t('nav:production.workOrders') },
    ],
  },
  {
    key: 'finance-group',
    label: t('nav:finance.title'),
    icon: <AccountBookOutlined />,
    children: [
      { key: '/receivables', icon: <WalletOutlined />, label: t('nav:finance.receivables') },
      { key: '/payables', icon: <CreditCardOutlined />, label: t('nav:finance.payables') },
      { key: '/receipt-vouchers', icon: <BankOutlined />, label: t('nav:finance.receiptVouchers') },
      { key: '/payment-vouchers', icon: <DollarOutlined />, label: t('nav:finance.paymentVouchers') },
      { key: '/aging-reports', icon: <BarChartOutlined />, label: t('nav:finance.agingReports') },
    ],
  },
  {
    key: 'reports-group',
    label: t('nav:reports.title'),
    icon: <BarChartOutlined />,
    children: [
      { key: '/reports', icon: <BarChartOutlined />, label: t('nav:reports.dashboard') },
      { key: '/reports/inventory', icon: <PieChartOutlined />, label: t('nav:reports.inventory') },
      { key: '/reports/sales', icon: <LineChartOutlined />, label: t('nav:reports.sales') },
      { key: '/reports/fulfillment', icon: <FundOutlined />, label: t('nav:reports.fulfillment') },
      { key: '/reports/production', icon: <BarChartOutlined />, label: t('nav:reports.production') },
      { key: '/reports/aging/ar', icon: <WalletOutlined />, label: t('nav:reports.arAging') },
      { key: '/reports/aging/ap', icon: <CreditCardOutlined />, label: t('nav:reports.apAging') },
    ],
  },
  {
    key: 'documents-group',
    label: t('nav:documents.title'),
    icon: <PrinterOutlined />,
    children: [
      { key: '/documents/templates', icon: <FileTextOutlined />, label: t('nav:documents.templates') },
    ],
  },
];

/**
 * Page title lookup from pathname - returns translation keys
 */
const PAGE_TITLE_KEYS: Record<string, string> = {
  '/materials': 'nav:masterData.materials',
  '/customers': 'nav:masterData.customers',
  '/suppliers': 'nav:masterData.suppliers',
  '/warehouses': 'nav:masterData.warehouses',
  '/departments': 'nav:masterData.departments',
  '/employees': 'nav:masterData.employees',
  '/categories': 'nav:masterData.categories',
  '/units-of-measure': 'nav:masterData.unitsOfMeasure',
  '/inbound-documents': 'nav:inventory.inbound',
  '/outbound-documents': 'nav:inventory.outbound',
  '/counting-documents': 'nav:inventory.stockCounting',
  '/inventory-alerts': 'nav:inventory.alerts',
  '/inventory-balances': 'nav:inventory.balance',
  '/sales-orders': 'nav:sales.orders',
  '/shipments': 'nav:sales.shipments',
  '/quotations': 'nav:sales.quotations',
  '/price-lists': 'nav:sales.priceLists',
  '/special-price-requests': 'nav:sales.specialPriceRequests',
  '/returns': 'nav:sales.returns',
  '/statements': 'nav:sales.statements',
  '/purchase-orders': 'nav:procurement.orders',
  '/purchase-receipts': 'nav:procurement.receipts',
  '/purchase-returns': 'nav:procurement.returns',
  '/production/boms': 'nav:production.boms',
  '/production': 'nav:production.workOrders',
  '/receivables': 'nav:finance.receivables',
  '/payables': 'nav:finance.payables',
  '/receipt-vouchers': 'nav:finance.receiptVouchers',
  '/payment-vouchers': 'nav:finance.paymentVouchers',
  '/aging-reports': 'nav:finance.agingReports',
  '/documents/templates': 'nav:documents.templates',
  '/documents/templates/new': 'nav:documents.templates',
  '/print': 'nav:documents.title',
  '/reports': 'nav:reports.dashboard',
  '/reports/inventory': 'nav:reports.inventory',
  '/reports/sales': 'nav:reports.sales',
  '/reports/fulfillment': 'nav:reports.fulfillment',
  '/reports/production': 'nav:reports.production',
  '/reports/aging/ar': 'nav:reports.arAging',
  '/reports/aging/ap': 'nav:reports.apAging',
};

function getPageTitleKey(pathname: string): string {
  if (PAGE_TITLE_KEYS[pathname]) return PAGE_TITLE_KEYS[pathname];
  const matchedKey = Object.keys(PAGE_TITLE_KEYS).find(key => pathname.startsWith(key + '/'));
  return matchedKey ? PAGE_TITLE_KEYS[matchedKey] : 'common:app.title';
}

/**
 * Routes that use prefix-based matching in ROLE_ROUTE_MAP.
 * These routes grant access to all their sub-routes (e.g., /production grants /production/*).
 * IMPORTANT: /reports is NOT a prefix route - each report route must be explicitly granted.
 */
const PREFIX_ROUTES: ReadonlySet<string> = new Set(['/production']);

/**
 * Filter menu items by user role using the role-route visibility matrix.
 * SYSTEM_ADMIN sees all groups. Other roles see only groups where they
 * have access to at least one child route.
 *
 * Note: divider items are stripped to avoid leading/trailing dividers
 * when filtering narrows the child list.
 *
 * Matching strategy:
 * - PREFIX_ROUTES (e.g., /production): Use prefix matching to grant access to sub-routes
 * - All other routes (e.g., /reports/*): Use exact matching to prevent over-authorization
 *
 * This prevents /reports (dashboard) from implicitly granting access to /reports/sales,
 * /reports/inventory, etc. Each report route must be explicitly listed in ROLE_ROUTE_MAP.
 */
function filterMenuItemsByRole(
  items: MenuProps['items'],
  role: string | undefined,
): MenuProps['items'] {
  if (!items || !role || role === 'SYSTEM_ADMIN') return items;

  // Validate role at runtime before indexing ROLE_ROUTE_MAP to avoid silent empty menus
  // for unknown JWT roles (e.g., future values or tampered tokens)
  const allowedRoutes = VALID_ROLES.has(role)
    ? (ROLE_ROUTE_MAP[role as EmployeeRole] ?? [])
    : [];

  /**
   * Check if a menu item key is accessible to the current role.
   * Uses hybrid matching: prefix for known prefix routes, exact for others.
   */
  const hasAccess = (key: string): boolean => {
    // First check exact match (covers explicitly granted routes)
    if (allowedRoutes.includes(key)) return true;

    // Then check prefix match for known prefix routes only
    for (const prefix of PREFIX_ROUTES) {
      if (allowedRoutes.includes(prefix) && key.startsWith(`${prefix}/`)) {
        return true;
      }
    }

    return false;
  };

  return items
    .map((item) => {
      if (!item || !('children' in item) || !item.children) return item;

      // Keep only route items accessible to this role (drop dividers when filtering)
      const accessibleChildren = item.children.filter((child) => {
        if (!child) return false;
        if (!('key' in child) || typeof child.key !== 'string') return false;
        if (!child.key.startsWith('/')) return false;
        return hasAccess(child.key);
      });

      if (accessibleChildren.length === 0) return null;
      return { ...item, children: accessibleChildren };
    })
    .filter(Boolean) as MenuProps['items'];
}

/**
 * Filter menu items by search query
 */
function filterMenuItems(items: MenuProps['items'], query: string): MenuProps['items'] {
  if (!query || !items) return items;
  const lowerQuery = query.toLowerCase();

  return items
    .map((item) => {
      if (!item || !('children' in item) || !item.children) return null;
      const filteredChildren = item.children.filter((child) => {
        if (!child || !('label' in child)) return false;
        return String(child.label).toLowerCase().includes(lowerQuery);
      });
      if (filteredChildren.length === 0) return null;
      return { ...item, children: filteredChildren };
    })
    .filter(Boolean) as MenuProps['items'];
}

const OPEN_KEYS_STORAGE_KEY = 'erp_nav_open_keys';

function getInitialOpenKeys(): string[] {
  try {
    const stored = localStorage.getItem(OPEN_KEYS_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return ALL_GROUP_KEYS;
}

/**
 * Main Layout Component
 * Provides navigation menu and consistent layout for the application
 */
export const MainLayout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openKeys, setOpenKeys] = useState<string[]>(getInitialOpenKeys);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const user = useAuthStore((state) => state.user);
  const clearAuth = useAuthStore((state) => state.clearAuth);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await authService.logout();
    } catch {
      // Ignore logout API errors - we'll clear local state anyway
    }
    clearAuth();
    navigate('/login', { replace: true });
  }, [clearAuth, navigate]);

  const handleMenuClick = useCallback(({ key }: { key: string }) => {
    navigate(key);
    setMobileDrawerOpen(false);
  }, [navigate]);

  const handleOpenChange = useCallback((keys: string[]) => {
    setOpenKeys(keys);
    try {
      localStorage.setItem(OPEN_KEYS_STORAGE_KEY, JSON.stringify(keys));
    } catch {
      // ignore
    }
  }, []);

  const handleSearchFilter = useCallback((query: string) => {
    setSearchQuery(query);
    if (query) {
      setOpenKeys(ALL_GROUP_KEYS);
    }
  }, []);

  const handleExpandSidebar = useCallback(() => {
    setCollapsed(false);
  }, []);

  // Get menu items with translations
  const menuItems = useMemo(() => getMenuItems(t), [t]);

  // Filter menu items by user role (SYSTEM_ADMIN sees all)
  const roleFilteredMenuItems = useMemo(
    () => filterMenuItemsByRole(menuItems, user?.role),
    [menuItems, user?.role],
  );

  const filteredMenuItems = useMemo(
    () => filterMenuItems(roleFilteredMenuItems, searchQuery),
    [roleFilteredMenuItems, searchQuery]
  );

  const selectedKey = location.pathname;

  // Get translated page title
  const pageTitle = t(getPageTitleKey(location.pathname));

  // User dropdown menu items
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: t('common:auth.changePassword'),
      onClick: () => { setChangePasswordOpen(true); },
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('common:auth.logout'),
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="lg"
          collapsedWidth={80}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
          }}
        >
          <div
            style={{
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#001529',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: collapsed ? '16px' : '20px',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            {collapsed ? 'ERP' : t('common:app.title')}
          </div>
          <NavSearch
            onFilter={handleSearchFilter}
            collapsed={collapsed}
            onExpandSidebar={handleExpandSidebar}
          />
          <div style={{ paddingBottom: 48 }}>
            <Menu
              mode="inline"
              selectedKeys={[selectedKey]}
              openKeys={collapsed ? [] : openKeys}
              onOpenChange={handleOpenChange}
              items={filteredMenuItems}
              onClick={handleMenuClick}
            />
          </div>
        </Sider>
      )}

      {/* Main Content Area */}
      <Layout
        style={{
          marginLeft: isMobile ? 0 : (collapsed ? 80 : 200),
          transition: 'margin-left 0.2s',
        }}
      >
        <Header
          style={{
            padding: isMobile ? '0 12px' : '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 6 : 0,
            height: isMobile ? 56 : 64,
            lineHeight: isMobile ? '56px' : '64px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          {isMobile && (
            <Button
              type="text"
              icon={<BarsOutlined />}
              onClick={() => setMobileDrawerOpen(true)}
              style={{ minHeight: 44, minWidth: 44, flex: '0 0 44px' }}
            />
          )}
          <div
            style={{
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            <span
              style={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: isMobile ? 18 : 20,
                fontWeight: 500,
                lineHeight: isMobile ? '24px' : '28px',
              }}
              title={pageTitle}
            >
              {pageTitle}
            </span>
          </div>
          <Space size={isMobile ? 4 : 'middle'} style={{ flex: '0 0 auto' }}>
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button
                type="text"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? 0 : 8,
                  minWidth: 44,
                  paddingInline: isMobile ? 8 : undefined,
                }}
                loading={isLoggingOut}
                aria-label={user?.employeeId ? `${t('common:auth.employeeId')}: ${user.employeeId}` : t('common:auth.employeeId')}
              >
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#1677ff' }}
                />
                {!isMobile && (
                  <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.employeeId || ''}
                  </span>
                )}
              </Button>
            </Dropdown>
            <LocaleSwitcher compact={isMobile} />
          </Space>
        </Header>

        {/*
          Content-container responsive width contract (goal-157/m1).
          Mobile (< md / 768px): a bounded 8px horizontal gutter and 12px padding
          give Outlet content the maximum usable width at 320-767 CSS pixels while
          keeping the rounded card edge off the viewport. The total shell chrome
          drops from 80px (16+24+24+16) to 40px (8+12+12+8) at 320px.
          Tablet/desktop: the existing 24px/16px margin and 24px padding are
          preserved unchanged so sidebar offsets, sticky header, and content
          behavior do not regress.
        */}
        <Content
          style={{
            margin: isMobile ? '12px 8px' : '24px 16px',
            padding: isMobile ? 12 : 24,
            minHeight: 280,
            background: '#fff',
            borderRadius: 8,
          }}
        >
          <Outlet />
        </Content>
      </Layout>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => { setChangePasswordOpen(false); }}
      />

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          title={t('nav:navigation')}
          placement="left"
          onClose={() => setMobileDrawerOpen(false)}
          open={mobileDrawerOpen}
          width={280}
          styles={{ body: { padding: 0 } }}
        >
          <NavSearch
            onFilter={handleSearchFilter}
            collapsed={false}
            tone="light"
          />
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            defaultOpenKeys={ALL_GROUP_KEYS}
            items={filteredMenuItems}
            onClick={handleMenuClick}
            style={{
              borderRight: 0,
            }}
          />
        </Drawer>
      )}
    </Layout>
  );
};
